import {
  FC,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { QuestionDropdownModel, RendererFactory } from "survey-core";
import { ReactQuestionFactory } from "survey-react-ui";
import { MultiSelectDownshift } from "../ui/multi-select-downshift";

interface MultiSelectQuestionProps {
  isDisplayMode: boolean;
  question: QuestionDropdownModel;
}

const MultiSelectQuestion: FC<MultiSelectQuestionProps> = ({ question }) => {
  const {
    allowClear,
    isRequired,
    otherItem,
    noneItem,
    hasOther,
    hasNone,
    isOtherSelected,
  } = question;

  const [textAreaInput, setTextAreaInput] = useState(question.comment);

  const onValueChange = useCallback(
    (newVal: string[]) => {
      question.value = newVal;
    },
    [question]
  );

  const getItems = useMemo(() => {
    const items = question.visibleChoices.map(({ title, value }) => ({
      title,
      value,
    }));
    if (hasNone) items.push(noneItem);
    if (hasOther) items.push(otherItem);
    if (allowClear) items.unshift({ value: undefined, title: "" });
    return items;
  }, [
    question.visibleChoices,
    hasNone,
    noneItem,
    hasOther,
    otherItem,
    allowClear,
  ]);

  // Ensure React re-evaluates getItems when SurveyJS updates visibleChoices
  useEffect(() => {
    const subscriptionKey = `visibleChoices:${question.getValueName()}`;
    const handler = () => {
      // no-op body; dependency on question.visibleChoices will trigger re-run
      // Force a micro state update to re-render in environments where array identity may be retained
      setTextAreaInput((prev) => prev);
    };
    question.registerPropertyChangedHandlers(
      ["visibleChoices"],
      handler,
      subscriptionKey
    );
    return () => {
      question.unregisterPropertyChangedHandlers(
        ["visibleChoices"],
        subscriptionKey
      );
    };
  }, [question]);

  return (
    <MultiSelectDownshift
      key={question.getValueName()}
      name={question.getValueName()}
      placeholder={question.placeholder}
      items={getItems}
      values={question.value ?? []}
      onValueChange={onValueChange}
      disabled={question.isReadOnly}
      hasOther={question.hasOther}
      hasNone={question.hasNone}
      commentPlaceHolder={question.commentPlaceholder}
      comment={textAreaInput}
      onCommentChange={setTextAreaInput}
      required={isRequired}
      isOtherSelected={isOtherSelected}
    />
  );
};

ReactQuestionFactory.Instance.registerQuestion(
  "sv-multi-select-question",
  function (props: any) {
    return createElement(MultiSelectQuestion, props);
  }
);

RendererFactory.Instance.registerRenderer(
  "dropdown",
  "multi-select-question",
  "sv-multi-select-question"
);
